/**
 * Form modal for capturing a new annotation snapshot. Wires
 * `useCreateSnapshot` (B7) into a small validated form.
 *
 * Composition mirrors `CreateCollectionModal` (PR-A):
 *   - controlled `open` / `onOpenChange`
 *   - auto-select-on-singleton for the taxonomy Select (per
 *     `feedback_no_taxonomy_default.md` — no hard-coded default)
 *   - manual validation with inline error messages
 *   - reset-on-close
 *   - `isPending` button gating
 */
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '../../../../../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../../../ui/dialog';
import { Input } from '../../../../../ui/input';
import { Label } from '../../../../../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../../ui/select';
import { Textarea } from '../../../../../ui/textarea';

import { useCreateSnapshot } from '../../hooks/useSnapshots';
import { useTaxonomyKeys } from '../../../hooks/useTaxonomy';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type FieldErrors = Partial<{
  taxonomyKey: string;
  version: string;
  label: string;
}>;

const SaveSnapshotModal: React.FC<Props> = ({ open, onOpenChange }) => {
  const createSnapshot = useCreateSnapshot();
  const { data: taxonomyKeys, isLoading: keysLoading } = useTaxonomyKeys();

  const [taxonomyKey, setTaxonomyKey] = useState('');
  const [version, setVersion] = useState('');
  const [label, setLabel] = useState('');
  const [notes, setNotes] = useState('');
  const [errors, setErrors] = useState<FieldErrors>({});

  // Auto-select-on-singleton (mirrors `CreateCollectionModal`): if the org
  // has exactly one taxonomy, pre-pick it. With multiple, the user must
  // choose explicitly — no hard-coded default per memory feedback.
  useEffect(() => {
    if (!taxonomyKey && taxonomyKeys && taxonomyKeys.length === 1) {
      setTaxonomyKey(taxonomyKeys[0].taxonomyKey);
    }
  }, [taxonomyKey, taxonomyKeys]);

  const resetForm = () => {
    setTaxonomyKey('');
    setVersion('');
    setLabel('');
    setNotes('');
    setErrors({});
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) resetForm();
    onOpenChange(nextOpen);
  };

  const handleSubmit = (e?: React.FormEvent) => {
    e?.preventDefault();
    const next: FieldErrors = {};
    if (!taxonomyKey) next.taxonomyKey = 'Select a taxonomy.';
    if (!version.trim()) next.version = 'Version is required.';
    if (!label.trim()) next.label = 'Label is required.';
    if (Object.keys(next).length > 0) {
      setErrors(next);
      return;
    }
    createSnapshot.mutate(
      {
        taxonomyKey,
        version: version.trim(),
        label: label.trim(),
        notes: notes.trim() || null,
      },
      {
        onSuccess: () => {
          toast.success('Snapshot saved');
          resetForm();
          onOpenChange(false);
        },
        onError: (err) => {
          toast.error(err.message || 'Failed to save snapshot');
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent data-testid="save-snapshot-modal">
        <DialogHeader>
          <DialogTitle>Save snapshot</DialogTitle>
          <DialogDescription>
            Capture the current annotation state as a recoverable baseline. The
            snapshot pins to the live version of the chosen taxonomy.
          </DialogDescription>
        </DialogHeader>

        <form
          id="save-snapshot-form"
          onSubmit={handleSubmit}
          className="space-y-4 py-2"
        >
          <div>
            <Label htmlFor="snapshot-taxonomy">
              Taxonomy <span className="text-red-500">*</span>
            </Label>
            <Select
              value={taxonomyKey}
              onValueChange={(v) => {
                setTaxonomyKey(v);
                setErrors((er) => ({ ...er, taxonomyKey: undefined }));
              }}
              disabled={keysLoading}
            >
              <SelectTrigger
                id="snapshot-taxonomy"
                className="mt-1"
                data-testid="snapshot-taxonomy-trigger"
              >
                <SelectValue
                  placeholder={
                    keysLoading ? 'Loading taxonomies…' : 'Select a taxonomy'
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {(taxonomyKeys ?? []).map((k) => (
                  <SelectItem key={k.taxonomyKey} value={k.taxonomyKey}>
                    {k.displayName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.taxonomyKey && (
              <p className="mt-1 text-sm text-red-600">{errors.taxonomyKey}</p>
            )}
          </div>

          <div>
            <Label htmlFor="snapshot-version">
              Version <span className="text-red-500">*</span>
            </Label>
            <Input
              id="snapshot-version"
              value={version}
              onChange={(e) => {
                setVersion(e.target.value);
                setErrors((er) => ({ ...er, version: undefined }));
              }}
              placeholder="e.g. 3.1"
              className="mt-1"
              data-testid="snapshot-version-input"
            />
            {errors.version && (
              <p className="mt-1 text-sm text-red-600">{errors.version}</p>
            )}
          </div>

          <div>
            <Label htmlFor="snapshot-label">
              Label <span className="text-red-500">*</span>
            </Label>
            <Input
              id="snapshot-label"
              value={label}
              onChange={(e) => {
                setLabel(e.target.value);
                setErrors((er) => ({ ...er, label: undefined }));
              }}
              placeholder="e.g. Post-vulnerability expansion"
              className="mt-1"
              data-testid="snapshot-label-input"
            />
            {errors.label && (
              <p className="mt-1 text-sm text-red-600">{errors.label}</p>
            )}
          </div>

          <div>
            <Label htmlFor="snapshot-notes">Notes</Label>
            <Textarea
              id="snapshot-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional — context for why this snapshot was captured."
              rows={3}
              className="mt-1"
              data-testid="snapshot-notes-input"
            />
          </div>
        </form>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={createSnapshot.isPending}
            data-testid="snapshot-cancel"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="save-snapshot-form"
            disabled={createSnapshot.isPending}
            data-testid="snapshot-submit"
          >
            {createSnapshot.isPending ? 'Saving…' : 'Save snapshot'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SaveSnapshotModal;
